import { useEffect, useState } from 'react';

import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { KimInput } from './kim-input';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateKimThread } from '@/api/mutations/useCreateKimThread';
import { BetaBadge } from '@/new/components/beta-badge';
import { Icon } from '@/new/components/icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

export function NewKimLargeInput() {
  const { matterId, subsetId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [question, setQuestion] = useState('');

  const { mutate: createThread, isLoading } = useCreateKimThread(matterId, subsetId);

  const handleSubmit = () => {
    const trimmed = question.trim();
    if (!trimmed) return;

    createThread(trimmed, {
      onSuccess: (data) => {
        navigate(`/matters/${matterId}/subsets/${subsetId}/new-kim/${data.id}`);
      },
      onError: () => {
        toast.error('Error creating thread');
      },
    });
  };

  useEffect(() => {
    const stateQuestion = (location.state as { question?: string } | null)?.question;
    if (stateQuestion?.trim()) {
      setQuestion(stateQuestion);
    }
  }, []);

  return (
    <div className="mb-12 flex w-full max-w-xl flex-col items-center">
      <Tooltip>
        <TooltipTrigger>
          <BetaBadge className="mb-3" />
        </TooltipTrigger>
        <TooltipContent sideOffset={14}>
          This version of Kim is still undergoing testing. Please send any feedback to{' '}
          <a href="mailto:support@wexler.ai" className="text-blue-500">
            support@wexler.ai
          </a>
          .
        </TooltipContent>
      </Tooltip>

      <h2 className="mb-8 font-serif text-2xl font-medium">Ask Kim...</h2>

      <KimInput
        className="min-h-32 w-full rounded-xl p-4 py-3"
        placeholder="What is this Matter about?"
        value={question}
        onChange={setQuestion}
        onSubmit={handleSubmit}
        disabled={isLoading}
      />

      <Link
        to={`/matters/${matterId}/subsets/${subsetId}/kim`}
        className="mt-5 flex items-center justify-center gap-1.5 text-center text-xs text-gray-400 duration-150 hover:text-gray-600"
        onClick={() => {
          trackCustomEvent(TrackingEventsV2.KimReturnToOldExperience, { matterId, subsetId });
          localStorage.setItem('isUsingNewKim', 'false');
        }}
      >
        <Icon className="lucide--undo-2 mb-0.5 -ml-1" /> Return to the old Kim experience
      </Link>
    </div>
  );
}
